import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import { BylineConnectionIconStack } from './BylineConnectionIconStack.tsx';
import {
	type BylineConnectionIcon,
	MAX_BYLINE_CONNECTION_ICONS,
} from './utils/getBylineConnectionIcons.ts';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
	teamworkGraph: {
		id: 'byline-connections.teamwork-graph-label',
		defaultMessage: 'Teamwork Graph',
		description: 'Label for the Teamwork Graph byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		whiteSpace: 'normal',
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
	},
});

const placeholderIcons: BylineConnectionIcon[] = Array.from(
	{ length: MAX_BYLINE_CONNECTION_ICONS },
	(_, index) => ({
		key: `placeholder-link-${index}`,
		label: '',
		icon: {
			kind: 'element',
			key: `placeholder-link-${index}`,
			label: '',
			node: (
				<LinkIcon
					label=""
					color={token('color.icon.disabled')}
					testId="byline-connections-ssr-placeholder-icon"
				/>
			),
		},
	}),
);

type BylineConnectionsPlaceholderVisualProps = {
	testId?: string;
};

/**
 * The disabled icon-stack + "Teamwork Graph" placeholder shown while the byline
 * connections item is resolving. Shared by the SSR/loadable placeholder and the
 * loaded component's own loading state so the footprint is pixel-identical
 * across the SSR → client-load → data-load → item handoff, avoiding layout
 * shift. `reserveMaximumWidth` keeps the icon stack at its full three-icon width.
 */
export const BylineConnectionsPlaceholderVisual = ({
	testId = 'byline-connections-ssr-placeholder',
}: BylineConnectionsPlaceholderVisualProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<span
			css={styles.wrapper}
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid={testId}
		>
			<span css={styles.trigger} aria-hidden="true">
				<BylineConnectionIconStack
					links={[]}
					additionalIcons={placeholderIcons}
					appearance="placeholder"
					reserveMaximumWidth
				/>
				{formatMessage(i18n.teamworkGraph)}
			</span>
		</span>
	);
};
